import { call, put, select, takeLatest } from 'redux-saga/effects';

import { QUOTE_STATUS } from '@frontend/api';
import {
  createStandaloneToast,
  Button,
  theme,
} from '@frontend/legacy-components';

import { getSelectedComponentsArray } from 'modules/quotes/quoteItemComponents/data/reducer';
import { quoteItemSelect } from 'modules/quotes/quoteItemNav/data/actions.js';
import { refreshExpandedQuoteItemSaga } from 'modules/quotes/quoteItemNav/data/sagas';
import { receiveQuoteItems } from 'modules/quotes/quoteItems/data/actions';
import { getQuoteItemFromComponentId } from 'modules/quotes/quoteItems/data/selectors';
import quoteItemService from 'modules/quotes/quoteItems/data/service';

import quoteService from 'services/quoteService';

import { checkFileStatusesForQuoteItems } from 'store/quotes/sagas';
import {
  getQuote,
  getDenormalizedQuoteItems,
  getQuoteItemIds,
} from 'store/quotes/selectors';
import { getGroupId } from 'store/session/selectors';

import history from 'utils/browserHistory';

import {
  copyPricing,
  getAdditionalQuotes,
  getAssemblyComponents,
  getCurrentQuoteItems,
  getDraftedQuotes,
  initCopyPricingDrawer,
  setIsChoosingQuote,
  types,
} from './actions';
import {
  getDestinationComponentsMap,
  getInitialQuote,
  getQuotesPage,
  getSelectedProperties,
  getSelectedQuote,
  getSourceComponent,
} from './reducer';

const { toast } = createStandaloneToast({ theme });

export default function* root() {
  yield takeLatest(types.OPEN_COPY_PRICING_DRAWER, watchOpenPricingDrawer);
  yield takeLatest(
    types.OPEN_ASSEMBLY_COPY_PRICING_DRAWER,
    watchOpenAssemblyPricingDrawer,
  );
  yield takeLatest(types.EXPAND_ASSEMBLY, watchExpandAssembly);
  yield takeLatest(types.CHOOSE_QUOTE, watchChooseQuote);
  yield takeLatest(types.SET_SELECTED_QUOTE, watchSetSelectedQuote);
  yield takeLatest(types.COPY_PRICING_REQUEST, watchCopyPricing);
  yield takeLatest(
    types.GET_ADDITIONAL_QUOTES_REQUEST,
    watchGetAdditionalQuotes,
  );
}

function* watchOpenPricingDrawer(action) {
  const { sourceComponentId } = action.payload;
  const quote = yield select(getQuote);
  const quoteItems = yield select(getDenormalizedQuoteItems);

  try {
    const sourceComponent = yield call(
      quoteItemService.components.get,
      sourceComponentId,
    );
    const sourceQuoteItem = yield select(
      getQuoteItemFromComponentId,
      sourceComponentId,
    );
    yield put(
      initCopyPricingDrawer({
        isSourceItar: sourceQuoteItem?.exportControlled,
        // The drawer holds quote list rows, so give the header the list field name
        quote: { ...quote, numberWithRevision: quote.quoteNumberWithRevision },
        quoteItems,
        sourceComponent,
      }),
    );
    if (quote.status !== QUOTE_STATUS.DRAFT) {
      yield call(watchChooseQuote);
    }
  } catch (e) {
    yield put(copyPricing.error(e));
  }
}

function* watchOpenAssemblyPricingDrawer() {
  const quote = yield select(getQuote);
  const quoteItems = yield select(getDenormalizedQuoteItems);
  const selectedComponents = yield select(getSelectedComponentsArray);
  try {
    const sourceComponent = yield call(
      quoteItemService.components.get,
      selectedComponents[0].id,
    );
    const sourceQuoteItem = yield select(
      getQuoteItemFromComponentId,
      selectedComponents[0].id,
    );
    yield put(
      initCopyPricingDrawer({
        isSourceItar: sourceQuoteItem?.exportControlled,
        // The drawer holds quote list rows, so give the header the list field name
        quote: { ...quote, numberWithRevision: quote.quoteNumberWithRevision },
        quoteItems,
        sourceComponent,
      }),
    );
    if (quote.status !== QUOTE_STATUS.DRAFT) {
      yield call(watchChooseQuote);
    }
  } catch (e) {
    yield put(copyPricing.error(e));
  }
}

function* watchChooseQuote() {
  try {
    yield put(setIsChoosingQuote({ isChoosingQuote: true }));
    const groupId = yield select(getGroupId);
    yield put(getDraftedQuotes.request());
    const response = yield call(quoteService.list, groupId, {
      status: 'draft',
    });
    yield put(
      getDraftedQuotes.success({
        draftedQuotes: response.results,
        hasMoreQuotes: Boolean(response.next),
      }),
    );
  } catch (error) {
    yield put(getDraftedQuotes.error(error));
  }
}

function* watchExpandAssembly(action) {
  try {
    const { quoteItem } = action.payload;
    yield put(
      getAssemblyComponents.request({
        rootComponentId: quoteItem.rootComponent.id,
      }),
    );
    const components = yield call(
      quoteItemService.briefComponentsList,
      quoteItem.id,
    );
    yield put(
      getAssemblyComponents.success({
        rootComponentId: quoteItem.rootComponent.id,
        components: components,
      }),
    );
  } catch (error) {
    yield put(getAssemblyComponents.error(error));
  }
}

function* watchSetSelectedQuote(action) {
  try {
    const { selectedQuote } = action.payload;
    yield put(getCurrentQuoteItems.request());
    const quoteItems = yield call(
      quoteService.getQuoteItemHeadersById,
      selectedQuote.id,
    );
    yield put(getCurrentQuoteItems.success({ quoteItems }));
  } catch (error) {
    yield put(getCurrentQuoteItems.error(error));
  }
}

function* watchCopyPricing() {
  try {
    const selectedQuote = yield select(getSelectedQuote);
    const initialQuote = yield select(getInitialQuote);
    const sourceComponent = yield select(getSourceComponent);
    const sourceComponentId = sourceComponent.id;
    const selectedProperties = yield select(getSelectedProperties);
    const destinationComponents = yield select(getDestinationComponentsMap);

    const componentNames = [];
    // NOTE: need this logic because you can copy to multiple different quotes
    let hasCopiedToCurrentQuote = false;
    const currentQuoteQuoteItemIds = yield select(getQuoteItemIds);
    for (const component of Object.values(destinationComponents)) {
      componentNames.push(
        component.filename || (component.part || {}).filename,
      );
      if (currentQuoteQuoteItemIds.includes(component.quoteItemId.toString())) {
        hasCopiedToCurrentQuote = true;
      }
    }

    const componentIds = Object.keys(destinationComponents);
    const data = {
      ...selectedProperties,
      isCopyingProfitItems: selectedProperties.isCopyingPricingItems,
      componentIds,
    };
    yield call(quoteService.copyPricing, sourceComponentId, data);
    yield put(copyPricing.success({ componentNames }));

    // choose the most recently selected from destination quote
    const isCopyingToOtherQuote = selectedQuote !== initialQuote;
    const destinationQuoteNumber = selectedQuote.numberWithRevision;
    const toastId = Math.random();

    toast({
      id: toastId,
      status: 'success',
      title: `Pricing copied to ${
        destinationComponents.length === 1 ? 'part' : 'parts'
      }`,
      description: isCopyingToOtherQuote && (
        <Button
          variant="link"
          size="sm"
          onClick={() => {
            history.push(`/quotes/edit/${destinationQuoteNumber}`);
            toast.close(toastId);
          }}
        >
          {`Go To Quote #${destinationQuoteNumber}`}
        </Button>
      ),
    });

    // if copying to active quote need to make sure we refresh UI
    if (hasCopiedToCurrentQuote) {
      // refresh the quote
      const quoteItems = yield call(
        quoteService.getQuoteItemHeadersById,
        initialQuote.id,
      );
      yield put(receiveQuoteItems(quoteItems));
      // since copy pricing can trigger interrogations, check for file statuses
      // to trigger polling life cycle
      yield call(checkFileStatusesForQuoteItems, quoteItems);
      yield call(refreshExpandedQuoteItemSaga);
    }

    yield put(quoteItemSelect.deselectAll());
  } catch (error) {
    yield put(copyPricing.error(error));
  }
}

function* watchGetAdditionalQuotes() {
  try {
    const groupId = yield select(getGroupId);
    const page = yield select(getQuotesPage);
    const response = yield call(quoteService.list, groupId, {
      status: 'draft',
      page: page + 1,
    });
    yield put(
      getAdditionalQuotes.success({
        quotes: response.results,
        page: page + 1,
        hasMoreQuotes: Boolean(response.next),
      }),
    );
  } catch (error) {
    yield put(getAdditionalQuotes.error(error));
  }
}
